<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
    <header class="max-w-3xl">
      <h1 class="text-xl font-bold leading-7 text-gray-900 md:text-3xl md:leading-9">{{ $t('general.my_account') }}</h1>
      <p class="mt-1 text-sm text-gray-600">{{ $t('myaccount.subtitle') }}</p>
    </header>

    <div class="mt-6 lg:mt-8 lg:grid lg:grid-cols-12 lg:gap-x-8">
      <!-- Secciones: pestañas desplazables en móvil, barra lateral fija en escritorio -->
      <aside class="lg:col-span-3">
        <nav :aria-label="$t('myaccount.sections')" class="lg:sticky lg:top-6">
          <ul class="-mx-4 flex gap-2 overflow-x-auto px-4 pb-2 snap-x lg:mx-0 lg:flex-col lg:gap-1 lg:overflow-visible lg:px-0 lg:pb-0">
            <li v-for="section in sections" :key="section.name" class="shrink-0 snap-start">
              <router-link :to="section.to" custom v-slot="{ href, navigate }">
                <a :href="href" @click="navigate" :aria-current="isActive(section) ? 'page' : null"
                   :class="isActive(section)
                     ? 'bg-white text-bpblue shadow-sm ring-1 ring-gray-200'
                     : 'text-gray-700 hover:bg-white/70 hover:text-gray-900'"
                   class="group flex items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue lg:whitespace-normal">
                  <svg :class="isActive(section) ? 'text-bpblue' : 'text-gray-400 group-hover:text-gray-500'"
                       class="h-5 w-5 flex-shrink-0 transition-colors duration-150 motion-reduce:transition-none"
                       xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" :d="section.icon"/>
                  </svg>
                  <span class="min-w-0">
                    <span class="block">{{ $t(section.label) }}</span>
                    <span class="hidden lg:block text-xs font-normal text-gray-500">{{ $t(section.description) }}</span>
                  </span>
                </a>
              </router-link>
            </li>
          </ul>
        </nav>
      </aside>

      <section class="mt-4 min-w-0 lg:col-span-9 lg:mt-0">
        <router-view></router-view>
      </section>
    </div>
  </div>
</template>

<script>
// Iconos Heroicons (outline, trazo 1.5), los mismos que ya usaba esta pantalla.
const ICONS = {
  profile: 'M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z',
  billingDetails: 'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z',
  subscription: 'M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z',
  plans: 'M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3',
  academies: 'M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z',
  about: 'm11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z',
};

export default {
  name: "NavigationMyAccountComponent",
  data() {
    return {
      sections: [
        { name: 'myaccount.profile', to: { name: 'myaccount.profile' }, label: 'general.my_profile', description: 'myaccount.profile_description', icon: ICONS.profile },
        { name: 'myaccount.billing-payments', to: { name: 'myaccount.billing-payments' }, label: 'myaccount.billing_details', description: 'myaccount.billing_details_description', icon: ICONS.billingDetails },
        { name: 'myaccount.billing', to: { name: 'myaccount.billing', params: { page: 1 } }, label: 'myaccount.my_subscription', description: 'myaccount.my_subscription_description', icon: ICONS.subscription },
        { name: 'myaccount.current-subscription', to: { name: 'myaccount.current-subscription' }, label: 'general.plans', description: 'myaccount.plans_description', icon: ICONS.plans },
        { name: 'myaccount.academies', to: { name: 'myaccount.academies' }, label: 'myaccount.my_academies', description: 'myaccount.my_academies_description', icon: ICONS.academies },
        { name: 'myaccount.information', to: { name: 'myaccount.information' }, label: 'myaccount.about', description: 'myaccount.about_description', icon: ICONS.about },
      ],
    }
  },
  methods: {
    // Por nombre de ruta: «Mi suscripción» sigue activa en cualquier página (/billing/2…).
    isActive(section) {
      return this.$route.name === section.name;
    },
  },
}
</script>
